// Anbefalingsmoduler: finner `.bits-recommend`-elementer (plassert i tema/CMS),
// henter ferdig rendrede produktkort fra api/bits-search/recommend og aktiverer
// den dependency-frie scroll-snap-slideren. Sender også atferdsbeacons
// (produktvisninger + anbefalingsklikk) til api/bits-search/track.
//
// Markup-kontrakt for temaer/CMS:
//   <div class="bits-recommend"
//        data-strategy="also-purchased"   (eller gammelt Clerk-templatenavn via data-template)
//        data-products="123,456"          (valgfritt; ellers sidekontekst)
//        data-category="42"               (valgfritt)
//        data-manufacturer="7"            (valgfritt; merkesider)
//        data-heading="Egen tittel"       (valgfritt; ellers innstilling/strategi-standard)
//        data-cart                        (valgfritt: ta med handlekurven som kontekst)
//        data-placement="popup|front|cart|category|manufacturer|campaign-cart" (valgfritt; front auto-detekteres)
//        data-limit="8"></div>
// Programmatisk (f.eks. popup-innhold): window.bitsRecommend.load(rootEl, { eager: true, cart: true, placement: 'popup' })
//
// Slideren (markup i _RecommendationSlider.cshtml, Swipers DOM-kontrakt), i prioritert
// rekkefølge etter at kortene er satt inn:
//   1. `bits-recommend:loaded` (bobler, cancelable) fyres på modulen. Kaller temaet
//      preventDefault(), starter det sin egen Swiper på markupen — samme konfigurasjon
//      som butikkens øvrige produktslidere. Nødvendig når Swiper er bundlet som
//      ES-modul i temaet og ikke finnes globalt.
//   2. Ellers, finnes `window.Swiper` (swiper-bundle, nops eget tema/Widgets.Swiper):
//      bundelen starter den selv med nøkterne standardvalg.
//   3. Ellers: dependency-fri scroll-snap på samme markup. Ingenting brekker.

const SKELETON_HTML =
    '<div class="bits-reco-skeleton">' +
    '<div class="bits-reco-skeleton-box"></div>'.repeat(4) +
    '</div>';

function isCategoryPage(): boolean {
    return !!document.querySelector('.html-category-page');
}

function pageContext(): { products: string | null; category: string | null } {
    const ctx: { products: string | null; category: string | null } = { products: null, category: null };

    if (document.querySelector('.product-details-page')) {
        ctx.products = document.querySelector('[data-productid]')?.getAttribute('data-productid') ?? null;
    }
    if (isCategoryPage()) {
        // data-category-id er fasettenes eget attributt (Facets.cshtml) og den
        // primære kilden; tomt på søkesiden. data-categoryid uten bindestrek er
        // SevenSpikes AjaxFilters — fallback for kald start, der kategorisiden
        // faller tilbake til standard nop-katalog med de gamle filtrene.
        const el = document.querySelector('[data-category-id]:not([data-category-id=""]), [data-categoryid]');
        ctx.category = el?.getAttribute('data-category-id') || el?.getAttribute('data-categoryid') || null;
    }

    return ctx;
}

interface LoadOptions {
    eager?: boolean;
    cart?: boolean;
    placement?: string | null;
}

function buildUrl(el: HTMLElement, opts: LoadOptions): string {
    const ctx = pageContext();
    const params = new URLSearchParams();

    const strategy = el.getAttribute('data-strategy') || el.getAttribute('data-template') || '';
    if (strategy) params.set('strategy', strategy);

    const products = el.getAttribute('data-products') || ctx.products;
    if (products) params.set('products', String(products).replace(/[[\]\s]/g, ''));

    const category = el.getAttribute('data-category') || ctx.category;
    if (category) params.set('category', category);

    // Merkesider: scoper populære/nyheter til produsenten. Settes av temaet eller
    // av pluginens egen modul — nop har ingen standard DOM-krok å lese den fra.
    const manufacturer = el.getAttribute('data-manufacturer');
    if (manufacturer) params.set('manufacturer', manufacturer);

    if (opts.cart || el.hasAttribute('data-cart')) params.set('cart', '1');

    const heading = el.getAttribute('data-heading');
    if (heading) params.set('heading', heading);

    // Plassering → serveren kan skru av flater via plugin-innstillingene.
    const placement = el.getAttribute('data-placement')
        || opts.placement
        || (document.documentElement.classList.contains('html-home-page') ? 'front' : null)
        || (isCategoryPage() ? 'category' : null);
    if (placement) params.set('placement', placement);

    params.set('limit', el.getAttribute('data-limit') || '8');

    return '/api/bits-search/recommend?' + params.toString();
}

/**
 * Butikkenes produktkort kommer ofte med utsatt bildelasting: den ekte URL-en står i
 * data-src (Swipers `swiper-lazy`, lazysizes o.l.) mens src peker på en placeholder.
 * Lasteren som skulle byttet dem har alt skannet siden når vi setter inn kortene, og
 * uten dette blir bildene stående som placeholder til noe annet utløser en ny skanning
 * — typisk at kunden scroller.
 *
 * Vi promoterer derfor data-src til src selv, og lar nettleserens egen loading="lazy"
 * ta seg av utsettelsen. Uavhengig av hvilken lazy-loader butikken bruker.
 */
function promoteLazyImages(root: ParentNode): void {
    root.querySelectorAll<HTMLImageElement>('img[data-src], img[data-srcset]').forEach((img) => {
        const src = img.getAttribute('data-src');
        const srcset = img.getAttribute('data-srcset');
        if (!src && !srcset) return;

        // Å sette src alene er ikke nok: temaene lar typisk bildet ligge på
        // opacity 0 og viser det først når lazy-loaderen har satt en klasse
        // (swiper-lazy-loaded / img-loaded). Fjerner vi data-src uten å sette
        // klassen, laster bildet — og forblir usynlig for alltid, også ved
        // scroll, siden loaderen ikke lenger finner noe å gjøre.
        const markLoaded = () => img.classList.add('swiper-lazy-loaded', 'img-loaded');
        if (img.complete && img.naturalWidth > 0) {
            markLoaded();
        } else {
            img.addEventListener('load', markLoaded, { once: true });
            // Et bilde som ikke lar seg laste skal ikke etterlate en evig spinner.
            img.addEventListener('error', markLoaded, { once: true });
        }

        if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
        if (srcset) {
            img.srcset = srcset;
            img.removeAttribute('data-srcset');
        }
        if (src) {
            img.src = src;
            img.removeAttribute('data-src');
        }
    });
}

interface SwiperLike {
    destroy?: () => void;
}

interface SwiperCtor {
    new (el: HTMLElement, options: Record<string, unknown>): SwiperLike;
}

const LOCK = 'swiper-button-lock';
const DISABLED = 'swiper-button-disabled';

interface SliderParts {
    swiper: HTMLElement;
    track: HTMLElement;
    prev: HTMLElement;
    next: HTMLElement;
    pagination: HTMLElement | null;
}

function sliderParts(el: HTMLElement): SliderParts | null {
    const swiper = el.querySelector<HTMLElement>('.bits-reco-swiper');
    const track = el.querySelector<HTMLElement>('.bits-reco-track');
    const prev = el.querySelector<HTMLElement>('.bits-reco-prev');
    const next = el.querySelector<HTMLElement>('.bits-reco-next');
    if (!swiper || !track || !prev || !next) return null;
    return { swiper, track, prev, next, pagination: el.querySelector<HTMLElement>('.bits-reco-pagination') };
}

/**
 * Global Swiper (swiper-bundle har alle moduler innebygd). Standardvalgene speiler
 * fallback-CSS-en: 1,4 kort på mobil, 3 fra 768px, 4 fra 1400px. Temaer som vil ha
 * noe annet lytter på `bits-recommend:loaded` og starter selv.
 */
function initSwiper(Swiper: SwiperCtor, parts: SliderParts): boolean {
    try {
        new Swiper(parts.swiper, {
            slidesPerView: 1.4,
            slidesPerGroup: 1,
            spaceBetween: 16,
            watchOverflow: true,
            navigation: { nextEl: parts.next, prevEl: parts.prev },
            pagination: parts.pagination ? { el: parts.pagination, clickable: true } : false,
            breakpoints: {
                768: { slidesPerView: 3, slidesPerGroup: 3 },
                1400: { slidesPerView: 4, slidesPerGroup: 4 },
            },
        });
        return true;
    } catch (e) {
        console.warn('BitsSearch: Swiper feilet, faller tilbake til scroll-snap', e);
        return false;
    }
}

/**
 * Dependency-fri fallback: .swiper-wrapper scroller med snap, pilene scrollBy.
 * Bruker Swipers egne tilstandsklasser på pilene (lock = ingenting å bla i,
 * disabled = i enden) så temaets Swiper-css gjelder også her.
 */
function initScrollSlider({ track, prev, next }: SliderParts): void {
    const step = () => Math.max(track.clientWidth * 0.8, 200);

    const update = () => {
        const overflow = track.scrollWidth > track.clientWidth + 4;
        prev.classList.toggle(LOCK, !overflow);
        next.classList.toggle(LOCK, !overflow);
        prev.classList.toggle(DISABLED, track.scrollLeft <= 4);
        next.classList.toggle(DISABLED, track.scrollLeft + track.clientWidth >= track.scrollWidth - 4);
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
}

function initSlider(el: HTMLElement): void {
    const parts = sliderParts(el);

    // Kortene er i DOM-en. Temaet får første rett til slideren: preventDefault()
    // betyr «jeg starter min egen Swiper». Fyres også for butikker som overstyrer
    // _RecommendationSlider.cshtml med helt egen markup (parts er null da).
    const claimed = !el.dispatchEvent(new CustomEvent('bits-recommend:loaded', {
        bubbles: true,
        cancelable: true,
        detail: { swiper: parts?.swiper ?? null },
    }));
    if (claimed || !parts) return;

    const Swiper = (window as unknown as { Swiper?: SwiperCtor }).Swiper;
    if (typeof Swiper === 'function' && initSwiper(Swiper, parts)) return;

    initScrollSlider(parts);
}

async function loadOne(el: HTMLElement, opts: LoadOptions = {}): Promise<void> {
    el.classList.add('bits-loaded');
    el.innerHTML = SKELETON_HTML;

    try {
        const response = await fetch(buildUrl(el, opts));

        // 204 = ingenting å anbefale (eller flaten er skrudd av): skjul modulen.
        if (response.status === 204 || !response.ok) {
            el.innerHTML = '';
            el.style.display = 'none';
            el.parentElement?.classList.add('bits-recommend-empty');
            return;
        }

        el.innerHTML = await response.text();
        promoteLazyImages(el);
        initSlider(el);
    } catch {
        el.innerHTML = '';
        el.style.display = 'none';
    }
}

// Utenfor viewport lastes modulene først når de nærmer seg (400px margin) —
// hver modul koster en kort-rendering på serveren.
const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer!.unobserve(entry.target);
            void loadOne(entry.target as HTMLElement);
        });
    }, { rootMargin: '400px' })
    : null;

function load(root: ParentNode = document, opts: LoadOptions = {}): void {
    root.querySelectorAll<HTMLElement>('.bits-recommend:not(.bits-loaded)').forEach((el) => {
        if (opts.eager || !observer) {
            void loadOne(el, opts);
        } else {
            observer.observe(el);
        }
    });
}

// --- Atferdssignaler (BS_ProductSignal): visninger og anbefalingsklikk. ---

function trackSignal(type: 'view' | 'click', productId: string | null | undefined, context?: string | null): void {
    const pid = Number(productId);
    if (!pid) return;

    const payload = JSON.stringify({ type, productId: pid, context: context || null });
    const sent = navigator.sendBeacon
        && navigator.sendBeacon('/api/bits-search/track', new Blob([payload], { type: 'application/json' }));
    if (!sent) {
        fetch('/api/bits-search/track', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
        }).catch(() => { /* best effort */ });
    }
}

// Produktside-visning etter 2s «dwell» — filtrerer bort umiddelbare tilbakehopp.
function trackProductView(): void {
    if (!document.querySelector('.product-details-page')) return;
    const pid = document.querySelector('[data-productid]')?.getAttribute('data-productid');
    if (!pid) return;
    window.setTimeout(() => trackSignal('view', pid), 2000);
}

// Klikk på anbefalingskort → CTR-signal med strategien som kontekst.
document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const slide = target?.closest('.bits-recommend .bits-reco-slide');
    if (!slide) return;
    const module = slide.closest('.bits-recommend');
    const pid = slide.querySelector('[data-productid]')?.getAttribute('data-productid');
    trackSignal('click', pid, module?.getAttribute('data-strategy') || module?.getAttribute('data-template'));
});

export function initRecommendations(): void {
    (window as unknown as { bitsRecommend?: { load: typeof load } }).bitsRecommend = { load };
    load();
    trackProductView();
}
